import { useAuth } from "@/auth/auth.ts";
import { orgRequestOptions } from "@/client/director/org.ts";
import useSWR from "swr";
import type { APIError, Limrun } from "@limrun/api";

type InstanceListArgs = {
	states: string[] | "all" | undefined;
	labelSelector?: string;
	startingAfter?: string;
	endingBefore?: string;
	enabled?: boolean;
};

type InstanceListParams = {
	state?: string;
	labelSelector?: string;
	startingAfter?: string;
	endingBefore?: string;
};

// The per-platform list hooks differ only in SWR key prefix and client
// resource, so one factory hosts the shared body. Key prefixes and the key
// shape [prefix, orgTid, state, labelSelector] must stay in sync with
// INSTANCE_LIST_SWR_KEYS and the refresh mutate in routes/instances.tsx.
// The organization is part of the key and pinned per request so that an org
// switch refetches and cannot cache one org's list under another org's key.
function makeInstanceListHook<Items>(
	swrKey: string,
	list: (
		client: Limrun,
		params: InstanceListParams,
		options?: ReturnType<typeof orgRequestOptions>,
	) => PromiseLike<Items>,
) {
	return function useInstanceList({
		states,
		labelSelector,
		startingAfter,
		endingBefore,
		enabled = true,
	}: InstanceListArgs): { data: Items | "loading"; error?: APIError } {
		const auth = useAuth();
		const state = states ? (states === "all" ? "all" : states.join(",")) : undefined;
		const { data, error } = useSWR(
			enabled
				? [swrKey, auth.organizationTid, state ?? "all", labelSelector ?? ""]
				: null,
			async () =>
				list(
					auth.regionClient,
					{
						...(startingAfter ? { startingAfter } : {}),
						...(endingBefore ? { endingBefore } : {}),
						...(state === "all" || state === undefined ? {} : { state }),
						...(labelSelector ? { labelSelector } : {}),
					},
					orgRequestOptions(auth.organizationTid),
				),
		);
		return {
			data: data ?? "loading",
			error,
		};
	};
}

export const useAndroidInstances = makeInstanceListHook<Limrun.AndroidInstancesItems>(
	"androidInstances",
	(client, params, options) => client.androidInstances.list(params, options),
);

export const useIosInstances = makeInstanceListHook<Limrun.IosInstancesItems>(
	"iosInstances",
	(client, params, options) => client.iosInstances.list(params, options),
);

export const useXcodeInstances = makeInstanceListHook<Limrun.XcodeInstancesItems>(
	"xcodeInstances",
	(client, params, options) => client.xcodeInstances.list(params, options),
);

export const useGradleInstances = makeInstanceListHook<Limrun.GradleInstancesItems>(
	"gradleInstances",
	(client, params, options) => client.gradleInstances.list(params, options),
);